/* Page overlay: menu links to /pages/* (About, FAQs...) open the page's content over the
   current page, like sayso.co's credits overlay. The pages stay real pages: the link keeps its
   href (no-JS, new tab, shared links and a direct load all get the normal page) and the URL
   changes to it while the overlay is open, so Back closes it and Forward reopens it.
   - Which links: #site-menu a[href^="/pages/"] (locale prefix included, e.g. "/fr/pages/"), except data-page-overlay="off" (Contact).
     Plain clicks only: ctrl/cmd/shift/alt/middle click keep the browser's behaviour.
   - Content: fetched from the page itself (the .site-page-body of templates/page.liquid, so it
     is the same markup), cached per path, prefetched when the menu opens and on hover/touch.
     A failed fetch falls back to following the link.
   - A modal <dialog>: top layer above the menu popover, aria-modal, page behind inert, Tab
     stays inside. Focus moves to the dialog; on close it goes back to the trigger, or to the
     menu button when the trigger was in the (now closed) menu. The page doesn't scroll behind.
   - Motion: sayso.co shutters, black halves meet over 520ms on its drawer curve, straight away
     on the click; the content fades in once they cover (or when it arrives, if it wasn't
     cached yet); close is the reverse. Reduced motion: a 160ms fade.
   - Close: × button, Esc, Back. The menu closes quietly behind the shutters once they cover.
   - Sound/haptics: the link press already plays select·press (sayso-sound.js); closing plays
     select·mech, the site's overlay/menu close.
   Public: window.saysoPageOverlay.open(href, trigger), .close(), .isOpen(). */
(() => {
  if (window.saysoPageOverlay || !window.HTMLDialogElement || !window.DOMParser) return;

  const SHUTTER_MS = 520;
  const FADE_MS = 160; // reduced motion
  // Locale-aware: "/pages/", or "/fr/pages/" on a /fr store (window.saysoRoutes from theme.liquid).
  const PAGES = `${(window.saysoRoutes?.root || "/").replace(/\/$/, "")}/pages/`;
  const LINKS = `#site-menu a[href^="${PAGES}"]:not([data-page-overlay="off"])`;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const cache = new Map(); // path -> Promise<{ title, html }>
  let dialog = null;
  let openPath = null;
  let returnTo = null;
  let restoreTitle = "";
  let timer = null;
  let originUrl = null; // where Back goes when the overlay was opened by a click

  const pathOf = (href) => {
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin ? url.pathname.replace(/\/$/, "") : null;
    } catch {
      return null;
    }
  };
  const eligible = (a) => Boolean(a && a.matches?.(LINKS) && pathOf(a.href));

  const load = (path) => {
    if (cache.has(path)) return cache.get(path);
    const job = fetch(path, { credentials: "same-origin", headers: { Accept: "text/html" } })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const body = doc.querySelector(".site-page-body");
        if (!body) throw new Error("no page content");
        const heading = body.querySelector(".site-page-title");
        const title = heading ? heading.textContent.trim() : doc.title;
        heading?.remove();
        return { title, html: body.innerHTML, docTitle: doc.title };
      });
    job.catch(() => cache.delete(path)); // try again next time
    cache.set(path, job);
    return job;
  };
  const prefetch = (a) => {
    if (eligible(a)) load(pathOf(a.href)).catch(() => {});
  };

  const build = () => {
    if (dialog) return dialog;
    dialog = document.createElement("dialog");
    dialog.className = "page-overlay";
    dialog.setAttribute("aria-labelledby", "page-overlay-title");
    dialog.tabIndex = -1;
    dialog.innerHTML =
      '<div class="page-overlay-shutter page-overlay-shutter--top" aria-hidden="true"></div>' +
      '<div class="page-overlay-shutter page-overlay-shutter--bottom" aria-hidden="true"></div>' +
      '<div class="page-overlay-scroll">' +
      '<article class="page-overlay-body"><h2 class="page-overlay-title" id="page-overlay-title"></h2>' +
      '<div class="page-overlay-content site-page-content"></div></article></div>' +
      '<button class="page-overlay-close" type="button" aria-label="Close">\u00d7</button>';
    dialog.querySelector(".page-overlay-close").addEventListener("click", () => requestClose());
    // Esc: our close (animated, and Back so the URL follows), not the dialog's instant one.
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      requestClose();
    });
    document.body.append(dialog);
    return dialog;
  };

  const lockScroll = (on) => document.documentElement.classList.toggle("page-overlay-locked", on);

  const closeMenuQuietly = () => {
    if (window.saysoPanels?.isOpen("site-menu")) {
      window.saysoPanels.close("site-menu", { restoreFocus: false, quiet: true });
    }
  };

  // Shutters close straight away; the content fills in when it's there (usually cached).
  const show = (path, label, trigger) => {
    const el = build();
    clearTimeout(timer);
    el.querySelector(".page-overlay-title").textContent = label;
    el.querySelector(".page-overlay-content").replaceChildren();
    el.querySelector(".page-overlay-scroll").scrollTop = 0;
    el.classList.toggle("is-reduced", reducedMotion.matches);
    el.classList.add("is-loading");
    el.setAttribute("aria-busy", "true");
    if (!openPath) {
      restoreTitle = document.title;
      returnTo = trigger || document.activeElement;
    }
    openPath = path;
    if (!el.open) {
      el.dataset.state = "";
      el.showModal();
      lockScroll(true);
      void el.offsetHeight; // start the shutters from open
    }
    el.dataset.state = "open";
    el.focus({ preventScroll: true });
    // The menu goes once the shutters cover it, without its own close sound.
    timer = setTimeout(() => openPath && closeMenuQuietly(), reducedMotion.matches ? FADE_MS : SHUTTER_MS);
  };

  const fill = ({ title, html, docTitle }) => {
    dialog.querySelector(".page-overlay-title").textContent = title;
    dialog.querySelector(".page-overlay-content").innerHTML = html;
    if (docTitle) document.title = docTitle;
    dialog.classList.remove("is-loading");
    dialog.removeAttribute("aria-busy");
  };

  const hide = () => {
    if (!dialog || !openPath) return;
    clearTimeout(timer);
    openPath = null;
    closeMenuQuietly(); // closed early (Back, Esc) before the shutters had covered it
    dialog.dataset.state = "closing";
    document.title = restoreTitle || document.title;
    window.saysoSound?.play("select", { theme: "mech" });
    window.saysoHaptic?.("light");
    const back = returnTo;
    returnTo = null;
    timer = setTimeout(() => {
      if (dialog.dataset.state !== "closing") return;
      dialog.close();
      dialog.dataset.state = "";
      lockScroll(false);
      // Back to the trigger, or the menu button when the trigger went with the menu.
      const target =
        back && back.isConnected && back.getClientRects().length ? back : document.querySelector(".site-menu-button");
      target?.focus({ preventScroll: true });
    }, reducedMotion.matches ? FADE_MS : SHUTTER_MS);
  };

  // Close from the overlay (×, Esc): step Back when we added the history entry, so the URL
  // goes back to the page underneath and Forward reopens; popstate does the closing.
  const requestClose = () => {
    if (!openPath) return;
    if (history.state?.saysoPageOverlay === openPath) history.back();
    else {
      if (originUrl) history.replaceState(history.state, "", originUrl);
      hide();
    }
  };

  const open = (href, trigger = null, { push = true } = {}) => {
    const path = pathOf(href);
    if (!path) return false;
    if (path === openPath) return true;
    const url = new URL(href, location.href);
    if (push) {
      if (!openPath) originUrl = location.href;
      const next = { saysoPageOverlay: path };
      if (openPath && history.state?.saysoPageOverlay) history.replaceState(next, "", url.href);
      else history.pushState(next, "", url.href);
    }
    show(path, trigger?.textContent.trim() || "", trigger);
    load(path).then(
      (content) => {
        if (openPath === path) fill(content);
      },
      () => {
        if (openPath === path) location.replace(url.href); // couldn't get it: just go to the page
      },
    );
    return true;
  };

  document.addEventListener("click", (event) => {
    const a = event.target.closest?.("a[href]");
    if (!eligible(a) || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (a.target && a.target !== "_self") return;
    if (pathOf(a.href) === pathOf(location.href)) return; // already on that page
    event.preventDefault();
    open(a.href, a);
  });

  window.addEventListener("popstate", (event) => {
    const path = event.state?.saysoPageOverlay;
    if (path && path === pathOf(location.href)) {
      if (!openPath) originUrl = null;
      open(location.href, null, { push: false });
    } else if (openPath) hide();
  });

  // While open, keys belong to the overlay: the builder's arrow keys, the menu's Esc etc.
  // must not act on the page underneath (Tab, scrolling keys and Esc's cancel still work).
  window.addEventListener(
    "keydown",
    (event) => {
      if (!openPath) return;
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
      }
      event.stopImmediatePropagation();
    },
    true,
  );

  // Prefetch: when the menu opens (idle), and on hover/touch/focus of a link.
  document.addEventListener(
    "site-panel:open",
    (event) => {
      if (event.target?.id !== "site-menu") return;
      const run = () => document.querySelectorAll(LINKS).forEach(prefetch);
      window.requestIdleCallback ? requestIdleCallback(run, { timeout: 800 }) : setTimeout(run, 200);
    },
    true,
  );
  ["pointerenter", "touchstart", "focusin"].forEach((type) =>
    document.addEventListener(type, (event) => prefetch(event.target.closest?.("a[href]")), {
      capture: true,
      passive: true,
    }),
  );

  // Back/forward cache: never come back to an open overlay.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted || !dialog?.open) return;
    clearTimeout(timer);
    openPath = null;
    dialog.close();
    dialog.dataset.state = "";
    lockScroll(false);
  });

  window.saysoPageOverlay = {
    open: (href, trigger) => open(href, trigger),
    close: requestClose,
    isOpen: () => Boolean(openPath),
  };
})();
